// Genuinely shared between src/App.jsx (Vite/browser, native ESM import) and functions/index.js
// (Node/Cloud Functions, CommonJS, loaded via dynamic import()) — confirmed feasible against this
// project's real build AND dev pipelines in the Phase A cross-module investigation and its follow-up
// implementation, with one correction along the way worth recording: a plain CommonJS (.cjs) module
// works fine with `vite build` (Rollup applies CJS interop to local files), but Vite's DEV SERVER
// does NOT — it serves local (non-node_modules) files straight to the browser's native ES module
// parser with no CJS-to-ESM transform, so a `module.exports = {...}` file exposes zero real ESM
// exports at dev-server runtime (confirmed via `npm run dev` throwing "does not provide an export
// named" for both named and default import forms). Writing this file as genuine ESM instead resolves
// that: `src/App.jsx` imports it natively (works identically in dev and build, no interop needed at
// all), and `functions/index.js` (which cannot `require()` an ESM file — that fails synchronously in
// Node) reaches it via `await import(...)`, which CommonJS code can call even though it can't
// `require()` ESM directly.
//
// This is the first real shared module between src/ and functions/ in this codebase — every prior
// case of matching logic (players/teams doc-to-record conversion in getAllPlayers/getAllTeams,
// sanitizeMessageHtml, resolveUserRole's role checks) was hand-duplicated instead, specifically
// because those functions lived in files with browser-only dependencies at the top. This function has
// no such dependency, so it qualifies — but note it also has no other npm/local dependencies of its
// own; keep it that way, since anything it imported would need to work under both Vite (browser) and
// Node (Cloud Functions) too.
//
// Determines which practice groups a player belongs to: playerExclusions vetoes membership outright
// (even if the player would otherwise match), playerOverrides force-includes a specific player in a
// specific group regardless of ageGroups, and ageGroups is the default/fallback match. Used
// client-side (Family Portal group display, roster-builder practice-group modal) and server-side
// (playerCalendarFeed's recurring practice VEVENT generation) — must stay identical on both sides,
// which sharing this file guarantees structurally instead of by hand-maintained comment.
//
// Node-scoping: only the ageGroups fallback branch is node-scoped — a player only auto-matches into
// a group in their own org node. playerOverrides/playerExclusions stay deliberately node-agnostic:
// overrides are the intentional cross-node escape hatch an admin uses to add an out-of-node player on
// purpose, and an exclusion should veto membership no matter which node either side is in. "root" is
// inlined as a literal (not imported) because this file is deliberately dependency-free (see header
// comment above) — it must stay in sync with ROOT_ORG_NODE_ID in src/App.jsx.
export function getPlayerPracticeGroups(player, allGroups) {
  const pid = String(player.id);
  const playerNodeId = player.nodeId || "root";
  return (allGroups || []).filter((g) => {
    if ((g.playerExclusions || []).includes(pid)) return false;
    if (!!(g.playerOverrides || {})[pid]) return true;
    const groupNodeId = g.nodeId || "root";
    return (g.ageGroups || []).includes(player.ageGroup) && groupNodeId === playerNodeId;
  });
}
